import React, { useEffect, useRef, useState } from "react";
import ShowDropdownIcon from "../../../../assets/icons/ShowDropdownIcon";
import { SearchIcon } from "../../../../assets/icons/searchIcon"; // You'll need to import or create a SearchIcon component
import cx from "classnames";

interface Option {
  value: string;
  label: string;
  name?: string;
  icon?: React.ReactNode;
}

export interface DropdownProps {
  options: Option[];
  name?: string;
  value?: string;
  error?: any;
  activeValue?: string;
  selectedValue?: string;
  setSelectedValue?: (value: string) => void;
  showOptions?: boolean;
  setShowOptions?: (value: boolean) => void;
  setCategoryError?: (value: string) => void;
  placeholder?: string;
  onChange?: (name: string, value: string) => void;
  onFilter?: (value: string) => void;
  isSubmitted?: boolean;
  dropdownClassName?: string;
  iconClassName?: string;
  disabled?: boolean;
  className?: string;
  label?: string;
  handlevariation?: (index: any, name: string, value: string) => void;
  variationIndex?: number;
  dropdownWidth?: string;
  styles?: {
    option: (
      styles: any,
      { data, isDisabled, isFocused, isSelected }: any,
    ) => any;
  };
  optionStyles?: {
    [key: string]: React.CSSProperties;
  };
  height?: string;
  enableSearch?: boolean;
}

const DropdownComponent: React.FC<DropdownProps> = ({
  name,
  options,
  value,
  error,
  placeholder,
  selectedValue,
  setSelectedValue,
  onChange,
  onFilter,
  isSubmitted,
  dropdownClassName,
  iconClassName,
  disabled,
  label,
  handlevariation,
  variationIndex,
  styles,
  optionStyles,
  activeValue,
  dropdownWidth,
  height = "h-[48px] rounded-lg",
  enableSearch = false,
}) => {
  const [showOptions, setShowOptions] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowOptions(false);
        setIsSearching(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownRef]);

  const filteredOptions = enableSearch
    ? options.filter((option) =>
        option.label.toLowerCase().includes(searchTerm.toLowerCase()),
      )
    : options;

  const iconContainerClassName = showOptions
    ? "absolute top-0 right-[5%] mt-4 md:mt-5 flex items-center"
    : "absolute top-0 right-[5%] mt-4 md:mt-5 flex items-center";

  // Added pl-8 for search icon padding

  const handleInputClick = () => {
    if (!disabled) {
      if (enableSearch) {
        setIsSearching(true);
        setShowOptions(true);
        setTimeout(() => inputRef.current?.focus(), 0);
      } else {
        setShowOptions(!showOptions);
      }
    }
  };

  const shouldShowSearchIcon = enableSearch && searchTerm;
  const inputPadding = shouldShowSearchIcon ? "pl-8" : "pl-2";

  const inputClassName = `border border-[#A4A4A4] placeholder:text-[16px] w-full placeholder-gray-500 p-2 focus:border-blue-600 focus:outline-none capitalize ${
    error && !selectedValue ? "ring-1 ring-red-500" : ""
  } ${dropdownClassName} ${
    selectedValue &&
    selectedValue !== "Select Status" &&
    selectedValue !== "Select Type"
      ? "text-black"
      : "text-[#98A2B3]"
  } ${styles ? "w-full" : ""} ${inputPadding}`;

  return (
    <div className="relative w-full" ref={dropdownRef}>
      <div className="relative w-full items-center">
        {/* Search icon (only shown when enableSearch is true) */}
        {shouldShowSearchIcon && (
          <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
            <SearchIcon />
          </div>
        )}

        {/* Main input field */}
        <input
          ref={inputRef}
          className={cx(`${height} ${inputClassName}`)}
          value={isSearching ? searchTerm : label || selectedValue}
          placeholder={placeholder}
          readOnly={!isSearching}
          disabled={disabled}
          style={{ width: dropdownWidth }}
          onChange={(e) => {
            if (enableSearch && isSearching) {
              setSearchTerm(e.target.value);
            }
          }}
          onClick={handleInputClick}
        />

        {/* Dropdown icon (only shown when not searching) */}
        {!enableSearch && (
          <div className={iconContainerClassName}>
            <ShowDropdownIcon iconClassName={iconClassName} />
          </div>
        )}
      </div>

      {showOptions && (
        <div
          className={`z-50 absolute shadow-md capitalize mt-[6px] rounded-[4px] ${dropdownClassName}`}
          style={{
            backgroundColor: "white",
            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
            width: "100%",
            padding: "0",
            listStyle: "none",
            maxHeight: "300px",
            overflowY: "auto",
          }}
        >
          <ul>
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => (
                <li
                  key={option.value}
                  className={`py-2 px-2 cursor-pointer items-center font-light flex justify-between focus:border-gray-300 !bg-white hover:!bg-gray-100 focus:outline-none text-[14px] ${
                    hoveredIndex === index ? "hover:!bg-gray-100" : ""
                  }`}
                  style={{
                    backgroundColor:
                      hoveredIndex === index ? "#E5E5E5" : "#F9F9F9",
                    transition: "background-color 0.3s",
                    fontWeight:
                      option.value === activeValue ? "bold" : "normal",
                  }}
                  onClick={() => {
                    setShowOptions(false);
                    setIsSearching(false);
                    if (setSelectedValue) {
                      setSelectedValue(option.value);
                    }
                    if (onChange) {
                      onChange(name || "", option.value);
                    }
                    if (onFilter) {
                      onFilter(option.value);
                    }
                    if (handlevariation) {
                      handlevariation(variationIndex, name || "", option.value);
                    }
                  }}
                  onMouseEnter={() => setHoveredIndex(index)}
                  onMouseLeave={() => setHoveredIndex(null)}
                >
                  <div className="flex items-center">
                    {option.icon && <div>{option.icon}</div>}
                    <p
                      className="block"
                      style={optionStyles && optionStyles[option.value]}
                    >
                      {option.label}
                    </p>
                  </div>
                  <span className="ml-2 ">{option.name}</span>
                </li>
              ))
            ) : (
              <li className="py-2 px-2 text-center text-gray-500">
                No options found
              </li>
            )}
          </ul>
        </div>
      )}
      {isSubmitted && !selectedValue && error && (
        <p className="text-red-500 absolute whitespace-nowrap text-[12px]">
          {error}
        </p>
      )}
    </div>
  );
};

export { DropdownComponent };
